import React, { useState, useEffect } from 'react';
import { TableSkeleton } from '../../../components/loading/skeletons';
import { adminListSmsLogs } from '../../api/smsLogsApi';
import './SMSLogs.css';

const SMSLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    companyId: '',
    startDate: '',
    endDate: '',
    success: ''
  });
  const [searchCompanyName, setSearchCompanyName] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [lastDocId, setLastDocId] = useState(null);

  const fetchLogs = async (isLoadMore = false) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        companyId: filters.companyId || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        limit: 50,
      };

      if (filters.success !== '') {
        params.success = filters.success === 'true';
      }

      if (isLoadMore && lastDocId) {
        params.lastDocId = lastDocId;
      }

      const data = await adminListSmsLogs(params);

      if (data.success) {
        if (isLoadMore) {
          setLogs((prevLogs) => [...prevLogs, ...data.logs]);
        } else {
          setLogs(data.logs);
        }
        setHasMore(data.hasMore);
        setLastDocId(data.lastDocId);
      } else {
        setError('Failed to fetch SMS logs');
      }
    } catch (err) {
      console.error('Error fetching SMS logs:', err);
      setError(err.message || 'Failed to fetch SMS logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLogs();
  };

  const handleReset = () => {
    setFilters({
      companyId: '',
      startDate: '',
      endDate: '',
      success: ''
    });
    setSearchCompanyName('');
    setLastDocId(null);
    setTimeout(() => fetchLogs(), 0);
  };

  const handleLoadMore = () => {
    fetchLogs(true);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const getStatusBadgeClass = (success) => {
    return success ? 'status-badge status-success' : 'status-badge status-failed';
  };

  const getMessageTypeLabel = (type) => {
    const typeMap = {
      'bookingConfirmation': 'Booking Confirmation',
      'appointmentReminder': 'Appointment Reminder',
      'scheduleChange': 'Schedule Change',
      'test': 'Test Message',
      'unknown': 'Other'
    };
    return typeMap[type] || type || 'Unknown';
  };

  // Filter logs by company name if search is active
  const filteredLogs = searchCompanyName
    ? logs.filter(log => 
        log.companyName?.toLowerCase().includes(searchCompanyName.toLowerCase())
      )
    : logs;

  return (
    <div className="admin-page sms-logs-container">
      <div className="sms-logs-header">
        <h1>SMS Logs</h1>
        <p className="sms-logs-subtitle">View and monitor all SMS messages sent by the system</p>
      </div>

      <div className="sms-logs-filters">
        <form onSubmit={handleSearch}>
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="companyId">Company ID</label>
              <input
                id="companyId"
                type="text"
                placeholder="Enter company ID"
                value={filters.companyId}
                onChange={(e) => setFilters({...filters, companyId: e.target.value})}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="companyName">Company Name</label>
              <input
                id="companyName"
                type="text"
                placeholder="Search by company name"
                value={searchCompanyName}
                onChange={(e) => setSearchCompanyName(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="startDate">Start Date</label>
              <input
                id="startDate"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="endDate">End Date</label>
              <input
                id="endDate"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="success">Status</label>
              <select
                id="success"
                value={filters.success}
                onChange={(e) => setFilters({...filters, success: e.target.value})}
              >
                <option value="">All</option>
                <option value="true">Success</option>
                <option value="false">Failed</option>
              </select>
            </div>
          </div>

          <div className="filter-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
            <button type="button" className="btn-secondary" onClick={handleReset}>
              Reset
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {loading && logs.length === 0 ? (
        <TableSkeleton rows={10} columns={[160, 180, 140, 120, 200, 80]} label="Loading SMS logs" />
      ) : (
        <>
          <div className="logs-count">
            Showing {filteredLogs.length} log{filteredLogs.length !== 1 ? 's' : ''}
          </div>

          <div className="table-wrap">
            <table className="data-table sms-logs-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Company</th>
                  <th>Recipient</th>
                  <th>Message</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Provider</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="no-logs">
                      No SMS logs found. Try adjusting your filters.
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => (
                    <tr key={log.id}>
                      <td className="timestamp-cell">
                        {formatDate(log.timestamp)}
                      </td>
                      <td className="company-cell">
                        <div className="company-info">
                          <span className="company-name">{log.companyName || 'N/A'}</span>
                          {log.companyId && (
                            <span className="company-id">{log.companyId}</span>
                          )}
                        </div>
                      </td>
                      <td className="phone-cell">
                        {formatPhoneNumber(log.to)}
                      </td>
                      <td className="message-cell">
                        <div className="message-text">{log.body}</div>
                        {log.errorMessage && (
                          <div className="error-detail">Error: {log.errorMessage}</div>
                        )}
                      </td>
                      <td className="type-cell">
                        <span className="type-badge">
                          {getMessageTypeLabel(log.messageType)}
                        </span>
                      </td>
                      <td className="status-cell">
                        <span className={getStatusBadgeClass(log.success)}>
                          {log.success ? '✓ Sent' : '✗ Failed'}
                        </span>
                      </td>
                      <td className="provider-cell">
                        {log.providerName || 'Unknown'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {hasMore && !searchCompanyName && (
            <div className="load-more-container">
              <button 
                className="btn-load-more" 
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SMSLogs;

